import React, { useEffect, useState } from "react";
import { useApp } from "../../contexts/AppContext";
import api from "../../lib/api";
import { toast } from "sonner";
import { Save, Snowflake, PartyPopper, Sparkles as SparkleIcon, Rocket, Image as ImageIcon, Eye, X } from "lucide-react";

const hexToRgba = (hex, a) => {
  const h = (hex || "#050a19").replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return `rgba(${parseInt(n.slice(0, 2), 16) || 0}, ${parseInt(n.slice(2, 4), 16) || 0}, ${parseInt(n.slice(4, 6), 16) || 0}, ${a})`;
};

function IntroPreview({ form, backendUrl, onClose }) {
  const img = form.intro_image_path_pt || form.intro_image_path_en;
  const src = img ? `${backendUrl}/api/files/${img}` : "";
  const enter = form.intro_enter_effect || "zoom-in";
  const overlay = Math.min(100, Math.max(0, Number(form.intro_overlay ?? 80))) / 100;
  const overlayColor = form.intro_overlay_color || "#050a19";
  const animSpeed = Math.max(0.3, Number(form.intro_anim_speed ?? 0.9));
  const isRetro = enter === "retro-explosion";
  const languageSelect = !!form.intro_language_select;
  const showBtn = form.intro_button_enabled !== false && !languageSelect;
  const featherOn = form.intro_feather_enabled !== false;
  const featherAmt = Math.min(45, Math.max(0, Number(form.intro_feather_amount ?? 10)));
  const tiltOn = form.intro_tilt_enabled !== false;
  const tiltX = Math.max(-30, Math.min(30, Number(form.intro_tilt_x ?? 4)));
  const tiltY = Math.max(-30, Math.min(30, Number(form.intro_tilt_y ?? -7)));
  const sizeW = Math.min(100, Math.max(20, Number(form.intro_image_size ?? 86)));
  const sizeH = Math.min(100, Math.max(20, Number(form.intro_image_height ?? 82)));
  const fit = form.intro_image_fit || "contain";
  const imgOpacity = Math.min(1, Math.max(0.1, Number(form.intro_image_opacity ?? 1)));
  const preset = form.intro_image_preset || "none";
  const blur = Math.min(20, Math.max(0, Number(form.intro_image_blur ?? 0)));
  const fullBg = form.intro_image_fullbg === true;
  const effSizeW = fullBg ? 100 : sizeW;
  const effSizeH = fullBg ? 100 : sizeH;
  const effFit = fullBg ? "cover" : fit;
  const presetFilter = ({
    none: "", vibrante: "saturate(150%) contrast(110%)", suave: "saturate(85%) brightness(105%)",
    bw: "grayscale(100%) contrast(105%)", sepia: "sepia(80%) saturate(120%)",
    noite: "hue-rotate(180deg) saturate(140%) brightness(90%)",
    sonho: "saturate(120%) brightness(108%) blur(1px)",
    quente: "sepia(30%) saturate(140%) hue-rotate(-10deg) brightness(105%)",
    frio: "hue-rotate(190deg) saturate(130%) brightness(95%)",
    neon: "saturate(200%) contrast(130%) brightness(110%)",
    retro: "sepia(60%) contrast(115%) saturate(130%) hue-rotate(-20deg)",
    cinema: "contrast(125%) saturate(120%) brightness(92%)",
    aurora: "hue-rotate(80deg) saturate(170%) brightness(105%)",
    oculto: "brightness(60%) contrast(130%) saturate(130%)",
    inverso: "invert(100%)",
    pastel: "saturate(70%) brightness(110%) contrast(90%)",
  })[preset] || "";
  const featherMask = `linear-gradient(to right, transparent 0, #000 ${featherAmt}%, #000 ${100 - featherAmt}%, transparent 100%), linear-gradient(to bottom, transparent 0, #000 ${featherAmt}%, #000 ${100 - featherAmt}%, transparent 100%)`;
  const imgStyle = {
    animationDuration: `${animSpeed}s`,
    "--intro-img-max-w": `${effSizeW}vw`,
    "--intro-img-max-h": `${effSizeH}vh`,
    "--intro-img-fit": effFit,
    filter: `${presetFilter} ${blur > 0 ? `blur(${blur}px)` : ""} drop-shadow(0 0 34px rgba(255,255,255,0.14)) drop-shadow(0 22px 60px rgba(0,0,0,0.5))`.trim(),
    ...(featherOn
      ? { WebkitMaskImage: featherMask, maskImage: featherMask, WebkitMaskComposite: "source-in", maskComposite: "intersect" }
      : { WebkitMaskImage: "none", maskImage: "none" }),
  };
  const frameStyle = { transform: tiltOn ? `perspective(1400px) rotateX(${tiltX}deg) rotateY(${tiltY}deg)` : "none", opacity: imgOpacity };
  return (
    <div className={`intro-splash ${form.intro_image_fullbg === true ? "intro-fullbg" : ""}`} style={{ background: hexToRgba(overlayColor, overlay) }} onClick={onClose} data-testid="intro-preview">
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="intro-preview-close"
        className="absolute top-5 right-5 z-[5] inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 text-[#0B1D3A] font-bold shadow-lg">
        <X size={16} /> Fechar pré-visualização
      </button>
      {isRetro && (
        <div className="intro-rays" aria-hidden>
          <span className="intro-sunburst" style={{ animationDuration: `${animSpeed}s` }} />
        </div>
      )}
      {isRetro && <span className="intro-flash intro-flash-in" aria-hidden />}
      {src
        ? <div className="intro-img-frame" style={frameStyle}><img src={src} alt="" style={imgStyle} className={`intro-img intro-enter-${enter}`} /></div>
        : <div className="relative z-[1] text-white/80 font-bold text-lg">Carregue uma imagem da intro para pré-visualizar</div>}
      {languageSelect && (
        <div className="intro-lang-wrap">
          {(form.intro_caption_pt ?? "Escolhe o teu idioma") && <p className="intro-caption">{form.intro_caption_pt ?? "Escolhe o teu idioma"}</p>}
          <div className="intro-lang-row">
            <button type="button" className="intro-lang-btn intro-lang-pt" onClick={(e) => { e.stopPropagation(); onClose(); }}>Português</button>
            <button type="button" className="intro-lang-btn intro-lang-en" onClick={(e) => { e.stopPropagation(); onClose(); }}>English</button>
          </div>
        </div>
      )}
      {showBtn && (
        <button type="button" className="intro-enter-btn" onClick={(e) => { e.stopPropagation(); onClose(); }}>
          {form.intro_button_text_pt || "Entrar"}
        </button>
      )}
    </div>
  );
}

export default function AdminEffects() {
  const { settingsRaw: settings, refreshSettings } = useApp();
  const [form, setForm] = useState(settings);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const openPreview = () => { setPreviewKey((k) => k + 1); setPreviewOpen(true); };
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/admin/settings/base").then((r) => setForm(r.data)).catch(() => setForm(settings));
  }, [settings]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file, field) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.path }));
      toast.success("Imagem carregada");
    } catch (e) { toast.error("Upload falhou"); }
  };

  const save = async () => {
    try {
      await api.put("/admin/settings", form);
      toast.success("Efeitos guardados");
      await refreshSettings();
    } catch (e) { toast.error("Erro"); }
  };

  const BgUploader = ({ label, field }) => (
    <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5">
      <p className="font-outfit font-semibold text-[#0B1D3A] mb-3 flex items-center gap-2"><ImageIcon size={18}/> {label}</p>
      <div className="flex items-center gap-3">
        <input type="file" accept="image/*"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], field)} className="text-sm" />
        {form[field] && (
          <>
            <img src={`${backendUrl}/api/files/${form[field]}`} alt="" className="h-16 w-24 object-cover rounded-lg border" />
            <button onClick={() => set(field, "")} className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-600">Remover</button>
          </>
        )}
      </div>
    </div>
  );

  const Toggle = ({ label, field, icon: Icon, color, testId, defaultOn }) => {
    const on = form[field] === undefined ? !!defaultOn : !!form[field];
    return (
    <div className="flex items-center justify-between rounded-2xl bg-white p-5 border border-[#0B1D3A]/5">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: color + "15", color }}>
          <Icon size={20}/>
        </span>
        <p className="font-outfit font-semibold text-[#0B1D3A]">{label}</p>
      </div>
      <button onClick={() => set(field, !on)} data-testid={testId}
        className={`w-14 h-8 rounded-full transition ${on ? "bg-[#005F73]" : "bg-[#0B1D3A]/15"} relative`}>
        <span className={`absolute top-1 ${on ? "left-7" : "left-1"} w-6 h-6 rounded-full bg-white transition-all`} />
      </button>
    </div>
    );
  };

  return (
    <div data-testid="admin-effects-page">
      <button onClick={save} data-testid="effects-save-floating"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#005F73] text-white font-bold shadow-2xl hover:bg-[#00485a] transition-colors">
        <Save size={18}/> Guardar Efeitos
      </button>
      <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A] mb-6">Efeitos</h1>

      <div className="space-y-4 max-w-2xl">
        <Toggle label="Neve a cair" field="snow_enabled" icon={Snowflake} color="#005F73" testId="toggle-snow-effect" />
        <Toggle label="Confetis (no final do quiz)" field="confetti_enabled" icon={PartyPopper} color="#FF6B6B" testId="toggle-confetti-effect" />
        <Toggle label="Fogo de artifício" field="fireworks_enabled" icon={Rocket} color="#FF8C42" testId="toggle-fireworks-effect" />
        <Toggle label="Estrelinhas de Natal" field="sparkles_enabled" icon={SparkleIcon} color="#FFD93D" testId="toggle-sparkles-effect" />
        <Toggle label="Nevão (neve intensa periódica)" field="blizzard_enabled" icon={Snowflake} color="#38BDF8" testId="toggle-blizzard-effect" />
        {form.blizzard_enabled && (
          <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block text-sm font-semibold">Repetir a cada (segundos)
                <input type="number" min="8" value={form.blizzard_interval ?? 30} onChange={(e) => set("blizzard_interval", parseInt(e.target.value) || 8)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="blizzard-interval" />
                <span className="text-xs text-[#0B1D3A]/50 font-normal">Intervalo entre cada nevão.</span>
              </label>
              <label className="block text-sm font-semibold">Duração do nevão (segundos)
                <input type="number" min="3" value={form.blizzard_duration ?? 8} onChange={(e) => set("blizzard_duration", parseInt(e.target.value) || 3)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="blizzard-duration" />
                <span className="text-xs text-[#0B1D3A]/50 font-normal">Tempo de neve intensa em cada ciclo.</span>
              </label>
              <label className="block text-sm font-semibold">Atraso inicial (segundos)
                <input type="number" min="0" value={form.blizzard_start_delay ?? 0} onChange={(e) => set("blizzard_start_delay", parseInt(e.target.value) || 0)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="blizzard-start-delay" />
                <span className="text-xs text-[#0B1D3A]/50 font-normal">Espera antes do primeiro nevão (ao abrir a página).</span>
              </label>
              <label className="block text-sm font-semibold">Intensidade (nº de flocos)
                <select value={form.blizzard_intensity || "medium"} onChange={(e) => set("blizzard_intensity", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="blizzard-intensity">
                  <option value="light">Leve</option>
                  <option value="medium">Médio</option>
                  <option value="strong">Forte</option>
                </select>
                <span className="text-xs text-[#0B1D3A]/50 font-normal">Densidade da neve durante o nevão.</span>
              </label>
              <label className="block text-sm font-semibold">Suavidade de entrada/saída (segundos)
                <input type="number" min="0.3" step="0.5" value={form.blizzard_fade ?? 3} onChange={(e) => set("blizzard_fade", parseFloat(e.target.value) || 0.3)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="blizzard-fade" />
                <span className="text-xs text-[#0B1D3A]/50 font-normal">Quanto maior, mais lentamente a neve surge e desaparece.</span>
              </label>
            </div>
            <div className="rounded-xl border border-[#0B1D3A]/10 p-4 space-y-3">
              <Toggle label="Intervalo aleatório (varia o tempo entre nevões)" field="blizzard_random" icon={Snowflake} color="#7C3AED" testId="toggle-blizzard-random" />
              {form.blizzard_random && (
                <label className="block text-sm font-semibold">Variação máxima (± segundos)
                  <input type="number" min="0" value={form.blizzard_random_max ?? 10} onChange={(e) => set("blizzard_random_max", parseInt(e.target.value) || 0)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="blizzard-random-max" />
                  <span className="text-xs text-[#0B1D3A]/50 font-normal">Ex.: 30s ± 10s → cada nevão surge entre 20s e 40s.</span>
                </label>
              )}
            </div>
            <Toggle label="Som suave de vento durante o nevão" field="blizzard_wind" icon={Snowflake} color="#0EA5E9" testId="toggle-blizzard-wind" />
          </div>
        )}

        <h2 className="font-outfit font-bold text-[#0B1D3A]/70 mt-6">Intro de entrada (imagem + zoom/explosão)</h2>
        <Toggle label="Ativar intro de entrada" field="intro_enabled" icon={ImageIcon} color="#7C3AED" testId="toggle-intro-enabled" />
        {form.intro_enabled && (
          <div className="space-y-3">
            <button type="button" onClick={openPreview} data-testid="intro-preview-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#7C3AED] text-white font-bold shadow hover:bg-[#6d28d9] transition-colors">
              <Eye size={18} /> Pré-visualizar intro
            </button>
            <BgUploader label="Imagem da intro (PT)" field="intro_image_path_pt" />
            <BgUploader label="Intro image (EN) — opcional (usa PT se vazio)" field="intro_image_path_en" />
            <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block text-sm font-semibold">Duração (segundos)
                <input type="number" min="0.5" step="0.5" value={form.intro_duration ?? 3} onChange={(e) => set("intro_duration", parseFloat(e.target.value) || 0.5)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-duration" />
                <span className="text-xs text-[#0B1D3A]/50 font-normal">Tempo visível antes de explodir.</span>
              </label>
              <label className="block text-sm font-semibold">Escurecimento do fundo (%)
                <input type="number" min="0" max="100" value={form.intro_overlay ?? 80} onChange={(e) => set("intro_overlay", parseInt(e.target.value) || 0)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-overlay" />
              </label>
              <label className="block text-sm font-semibold">Cor de fundo do overlay
                <input type="color" value={form.intro_overlay_color || "#050a19"} onChange={(e) => set("intro_overlay_color", e.target.value)} className="w-full mt-1 h-10 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-overlay-color" />
                <span className="text-xs text-[#0B1D3A]/50 font-normal">A opacidade vem do escurecimento acima.</span>
              </label>
              <label className="block text-sm font-semibold">Velocidade da animação (segundos)
                <input type="number" min="0.3" step="0.1" value={form.intro_anim_speed ?? 0.9} onChange={(e) => set("intro_anim_speed", parseFloat(e.target.value) || 0.3)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-anim-speed" />
                <span className="text-xs text-[#0B1D3A]/50 font-normal">Menor = explosão/entrada mais rápida.</span>
              </label>
              <label className="block text-sm font-semibold">Efeito de entrada
                <select value={form.intro_enter_effect || "zoom-in"} onChange={(e) => set("intro_enter_effect", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-enter">
                  <option value="zoom-in">Zoom in (predefinido)</option>
                  <option value="retro-explosion">Retroexplosão (raios + flash)</option>
                  <option value="fade">Aparecer (fade)</option>
                  <option value="drop">Cair de cima</option>
                  <option value="pop">Pop (saltar)</option>
                </select>
              </label>
              <label className="block text-sm font-semibold">Efeito de saída
                <select value={form.intro_exit_effect || "explosion"} onChange={(e) => set("intro_exit_effect", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-exit">
                  <option value="explosion">Explosão (predefinido)</option>
                  <option value="zoom-out">Zoom out</option>
                  <option value="fade">Desaparecer (fade)</option>
                  <option value="fall">Cair</option>
                </select>
              </label>
              <label className="block text-sm font-semibold">Frequência
                <select value={form.intro_frequency || "session"} onChange={(e) => set("intro_frequency", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-frequency">
                  <option value="session">Uma vez por sessão</option>
                  <option value="firstvisit">Só na 1.ª visita (não repete)</option>
                  <option value="always">Sempre que entra</option>
                </select>
                <span className="text-xs text-[#0B1D3A]/50 font-normal">"Uma vez por sessão" evita repetir ao navegar.</span>
              </label>
              <label className="block text-sm font-semibold">Texto do botão (PT)
                <input value={form.intro_button_text_pt ?? "Entrar"} onChange={(e) => set("intro_button_text_pt", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-button-pt" />
              </label>
              <label className="block text-sm font-semibold">Button text (EN)
                <input value={form.intro_button_text_en ?? "Enter"} onChange={(e) => set("intro_button_text_en", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-button-en" />
              </label>
            </div>
            <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-4" data-testid="intro-image-controls">
              <h3 className="font-outfit font-bold text-[#0B1D3A] flex items-center gap-2"><ImageIcon size={18} /> Tamanho & efeitos da imagem</h3>
              <label className="flex items-center gap-3 text-sm font-semibold cursor-pointer select-none">
                <input type="checkbox" checked={form.intro_image_fullbg === true}
                  onChange={(e) => set("intro_image_fullbg", e.target.checked)}
                  className="h-5 w-5 accent-[#005F73]" data-testid="intro-image-fullbg" />
                Usar imagem como fundo em ecrã inteiro
                <span className="font-normal text-xs text-[#0B1D3A]/50">(ignora Largura/Altura; botões de idioma ficam por cima)</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block text-sm font-semibold">Largura (% do ecrã): <span className="text-[#005F73]">{form.intro_image_size ?? 86}%</span>
                  <input type="range" min="20" max="100" step="1" value={form.intro_image_size ?? 86}
                    onChange={(e) => set("intro_image_size", parseInt(e.target.value))}
                    className="w-full mt-2 accent-[#005F73]" data-testid="intro-image-size" />
                  <span className="text-xs text-[#0B1D3A]/50 font-normal">100% = ocupa toda a largura do ecrã.</span>
                </label>
                <label className="block text-sm font-semibold">Altura máxima (% do ecrã): <span className="text-[#005F73]">{form.intro_image_height ?? 82}%</span>
                  <input type="range" min="20" max="100" step="1" value={form.intro_image_height ?? 82}
                    onChange={(e) => set("intro_image_height", parseInt(e.target.value))}
                    className="w-full mt-2 accent-[#005F73]" data-testid="intro-image-height" />
                </label>
                <label className="block text-sm font-semibold">Modo de ajuste
                  <select value={form.intro_image_fit || "contain"} onChange={(e) => set("intro_image_fit", e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 bg-white" data-testid="intro-image-fit">
                    <option value="contain">Encaixar (mantém proporção)</option>
                    <option value="cover">Preencher (corta se preciso)</option>
                    <option value="fill">Esticar (distorce)</option>
                  </select>
                  <span className="text-xs text-[#0B1D3A]/50 font-normal">Para ocupar o ecrã todo escolhe 100% + "Preencher".</span>
                </label>
                <label className="block text-sm font-semibold">Opacidade: <span className="text-[#005F73]">{Math.round((form.intro_image_opacity ?? 1) * 100)}%</span>
                  <input type="range" min="20" max="100" step="1"
                    value={Math.round((form.intro_image_opacity ?? 1) * 100)}
                    onChange={(e) => set("intro_image_opacity", parseInt(e.target.value) / 100)}
                    className="w-full mt-2 accent-[#005F73]" data-testid="intro-image-opacity" />
                </label>
                <label className="block text-sm font-semibold">Preset de efeito
                  <select value={form.intro_image_preset || "none"} onChange={(e) => set("intro_image_preset", e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 bg-white" data-testid="intro-image-preset">
                    <option value="none">Nenhum (original)</option>
                    <option value="vibrante">Vibrante</option>
                    <option value="suave">Suave</option>
                    <option value="pastel">Pastel</option>
                    <option value="neon">Néon</option>
                    <option value="cinema">Cinema</option>
                    <option value="quente">Quente (dourado)</option>
                    <option value="frio">Frio (gelado)</option>
                    <option value="aurora">Aurora boreal</option>
                    <option value="noite">Noite (azul mágico)</option>
                    <option value="oculto">Oculto (escurecido)</option>
                    <option value="sonho">Sonho (desfocado)</option>
                    <option value="retro">Retro</option>
                    <option value="bw">Preto & Branco</option>
                    <option value="sepia">Sépia</option>
                    <option value="inverso">Inverso</option>
                  </select>
                </label>
                <label className="block text-sm font-semibold">Desfoque: <span className="text-[#005F73]">{form.intro_image_blur ?? 0}px</span>
                  <input type="range" min="0" max="12" step="1" value={form.intro_image_blur ?? 0}
                    onChange={(e) => set("intro_image_blur", parseInt(e.target.value))}
                    className="w-full mt-2 accent-[#005F73]" data-testid="intro-image-blur" />
                </label>
              </div>
            </div>
            <Toggle label="Contornos suaves (desvanecer bordas)" field="intro_feather_enabled" icon={ImageIcon} color="#0EA5E9" testId="toggle-intro-feather" defaultOn />
            {form.intro_feather_enabled !== false && (
              <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block text-sm font-semibold md:col-span-2">Intensidade do desvanecer: <span className="text-[#0EA5E9]">{form.intro_feather_amount ?? 10}%</span>
                  <input type="range" min="0" max="35" step="1" value={form.intro_feather_amount ?? 10} onChange={(e) => set("intro_feather_amount", parseInt(e.target.value))} className="w-full mt-2 accent-[#0EA5E9]" data-testid="intro-feather-amount" />
                  <span className="text-xs text-[#0B1D3A]/50 font-normal">0% = bordas nítidas · mais alto = bordas mais esbatidas no fundo.</span>
                </label>
              </div>
            )}
            <Toggle label="Inclinação / perspetiva 3D da imagem" field="intro_tilt_enabled" icon={ImageIcon} color="#7C3AED" testId="toggle-intro-tilt" defaultOn />
            {form.intro_tilt_enabled !== false && (
              <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block text-sm font-semibold">Ângulo horizontal (Y): <span className="text-[#7C3AED]">{form.intro_tilt_y ?? -7}°</span>
                  <input type="range" min="-25" max="25" step="1" value={form.intro_tilt_y ?? -7} onChange={(e) => set("intro_tilt_y", parseInt(e.target.value))} className="w-full mt-2 accent-[#7C3AED]" data-testid="intro-tilt-y" />
                  <span className="text-xs text-[#0B1D3A]/50 font-normal">Roda para a esquerda/direita.</span>
                </label>
                <label className="block text-sm font-semibold">Ângulo vertical (X): <span className="text-[#7C3AED]">{form.intro_tilt_x ?? 4}°</span>
                  <input type="range" min="-20" max="20" step="1" value={form.intro_tilt_x ?? 4} onChange={(e) => set("intro_tilt_x", parseInt(e.target.value))} className="w-full mt-2 accent-[#7C3AED]" data-testid="intro-tilt-x" />
                  <span className="text-xs text-[#0B1D3A]/50 font-normal">Inclina para cima/baixo.</span>
                </label>
              </div>
            )}
            <Toggle label="Mostrar botão Entrar (pulsante)" field="intro_button_enabled" icon={ImageIcon} color="#FFD93D" testId="toggle-intro-button" />
            <Toggle label="Aguardar clique (não desaparece sozinho)" field="intro_require_click" icon={ImageIcon} color="#C1121F" testId="toggle-intro-require-click" />
            <Toggle label="Seleção de idioma (2 botões PT/EN; a imagem só desaparece após escolher)" field="intro_language_select" icon={ImageIcon} color="#2D9C5A" testId="toggle-intro-language-select" />
            {form.intro_language_select && (
              <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block text-sm font-semibold">Legenda acima dos botões (PT)
                  <input value={form.intro_caption_pt ?? "Escolhe o teu idioma"} onChange={(e) => set("intro_caption_pt", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-caption-pt" />
                </label>
                <label className="block text-sm font-semibold">Caption above buttons (EN)
                  <input value={form.intro_caption_en ?? "Choose your language"} onChange={(e) => set("intro_caption_en", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-caption-en" />
                </label>
                <label className="block text-sm font-semibold">1.º ecrã — botão Português
                  <input value={form.intro_btn_pt_label ?? "Português"} onChange={(e) => set("intro_btn_pt_label", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-btn-pt-label" />
                </label>
                <label className="block text-sm font-semibold">1.º ecrã — botão English
                  <input value={form.intro_btn_en_label ?? "English"} onChange={(e) => set("intro_btn_en_label", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-btn-en-label" />
                </label>
                <label className="block text-sm font-semibold">2.º ecrã (variante) — legenda (PT)
                  <input value={form.intro_variant_caption_pt ?? "Português — escolhe a variante"} onChange={(e) => set("intro_variant_caption_pt", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-variant-caption-pt" />
                </label>
                <label className="block text-sm font-semibold">2.º ecrã (variante) — legenda (EN)
                  <input value={form.intro_variant_caption_en ?? "Portuguese — choose the variant"} onChange={(e) => set("intro_variant_caption_en", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-variant-caption-en" />
                </label>
                <label className="block text-sm font-semibold">Botão — Português (Europa)
                  <input value={form.intro_variant_eu_label ?? "Português (Europa)"} onChange={(e) => set("intro_variant_eu_label", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-variant-eu-label" />
                </label>
                <label className="block text-sm font-semibold">Botão — Português (Brasil)
                  <input value={form.intro_variant_br_label ?? "Português (Brasil)"} onChange={(e) => set("intro_variant_br_label", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-variant-br-label" />
                </label>
                <label className="block text-sm font-semibold">Botão "Voltar" (PT)
                  <input value={form.intro_back_label_pt ?? "Voltar"} onChange={(e) => set("intro_back_label_pt", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-back-label-pt" />
                </label>
                <label className="block text-sm font-semibold">"Back" button (EN)
                  <input value={form.intro_back_label_en ?? "Back"} onChange={(e) => set("intro_back_label_en", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="intro-back-label-en" />
                </label>
              </div>
            )}
            <p className="text-xs text-[#0B1D3A]/50">A intro surge sobre um fundo escurecido com a imagem em zoom; ao fim do tempo definido (ou ao clicar) desaparece com o efeito escolhido.</p>
          </div>
        )}

        <h2 className="font-outfit font-bold text-[#0B1D3A]/70 mt-6">Botão de partilha / link externo (barra de navegação)</h2>
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-3" data-testid="share-button-controls">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={form.share_button_enabled !== false} onChange={(e) => set("share_button_enabled", e.target.checked)} data-testid="share-button-enabled" />
            Mostrar botão na barra de navegação
          </label>
          <label className="block text-sm">Etiqueta do botão
            <input value={form.share_button_label ?? ""} onChange={(e) => set("share_button_label", e.target.value)} placeholder="Builder Fest" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="share-button-label" />
          </label>
          <label className="block text-sm">Link (URL) — abre em novo separador
            <input value={form.share_button_url ?? ""} onChange={(e) => set("share_button_url", e.target.value)} placeholder="https://..." className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="share-button-url" />
          </label>
          <p className="text-xs text-[#0B1D3A]/45">Predefinido para a página do Builder Fest. Podes mudar o link para qualquer outro (ex.: redes sociais, imprensa).</p>
        </div>

        <h2 className="font-outfit font-bold text-[#0B1D3A]/70 mt-6">Cabeçalho (Ticker / Texto)</h2>
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.header_ticker_enabled} onChange={(e) => set("header_ticker_enabled", e.target.checked)} data-testid="ticker-enabled" />
            Ativar ticker (faixa rolante tipo TV)
          </label>
          <label className="block text-sm">Ticker (PT)
            <textarea value={form.header_ticker_pt || ""} onChange={(e) => set("header_ticker_pt", e.target.value)} rows="2" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15"/>
          </label>
          <label className="block text-sm">Ticker (EN)
            <textarea value={form.header_ticker_en || ""} onChange={(e) => set("header_ticker_en", e.target.value)} rows="2" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15"/>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-[#0B1D3A]/10" data-testid="ticker-style-controls">
            <label className="block text-sm">Velocidade ({form.header_ticker_speed ?? 40}s por volta — menor = mais rápido)
              <input type="range" min="5" max="600" value={form.header_ticker_speed ?? 40}
                onChange={(e) => set("header_ticker_speed", parseInt(e.target.value))}
                className="w-full mt-2 accent-[#005F73]" data-testid="ticker-speed-slider" />
            </label>
            <label className="block text-sm">Tipo de letra
              <select value={form.header_ticker_font || "Nunito"} onChange={(e) => set("header_ticker_font", e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="ticker-font-select">
                <option value="Nunito">Nunito</option>
                <option value="Fredoka">Fredoka</option>
                <option value="Outfit">Outfit</option>
                <option value="Poppins">Poppins</option>
                <option value="Georgia, serif">Georgia (serifa)</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="'Courier New', monospace">Courier (monoespaçada)</option>
                <option value="Impact, sans-serif">Impact</option>
              </select>
            </label>
            <label className="block text-sm">Tamanho da letra ({form.header_ticker_size ?? 16}px)
              <input type="range" min="10" max="48" value={form.header_ticker_size ?? 16}
                onChange={(e) => set("header_ticker_size", parseInt(e.target.value))}
                className="w-full mt-2 accent-[#005F73]" data-testid="ticker-size-slider" />
            </label>
            <label className="block text-sm">Cor de fundo
              <input type="color" value={form.header_ticker_bg || "#0B1D3A"} onChange={(e) => set("header_ticker_bg", e.target.value)}
                className="w-full mt-1 h-10 rounded-lg border border-[#0B1D3A]/15" data-testid="ticker-bg-color" />
            </label>
            <label className="block text-sm">Cor da letra
              <input type="color" value={form.header_ticker_color || "#C8A24B"} onChange={(e) => set("header_ticker_color", e.target.value)}
                className="w-full mt-1 h-10 rounded-lg border border-[#0B1D3A]/15" data-testid="ticker-text-color" />
            </label>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#0B1D3A]/50 mb-1.5">Pré-visualização</p>
            <div className="rounded-lg overflow-hidden" data-testid="ticker-preview"
              style={{ background: form.header_ticker_bg || "#0B1D3A", color: form.header_ticker_color || "#C8A24B" }}>
              <div className="py-2 whitespace-nowrap font-semibold px-4"
                style={{ fontFamily: form.header_ticker_font || "Nunito", fontSize: `${form.header_ticker_size ?? 16}px` }}>
                ★ {form.header_ticker_pt || "Texto da faixa rolante"} ★
              </div>
            </div>
          </div>

        </div>
      </div>
      {previewOpen && <IntroPreview key={previewKey} form={form} backendUrl={backendUrl} onClose={() => setPreviewOpen(false)} />}
    </div>
  );
}
